import type Lenis from 'lenis';

/**
 * Minimal interface for GSAP ScrollTrigger integration.
 * Avoids importing gsap into this bundle — enableScrollTriggerIntegration() is
 * called from home-hero.ts which owns the gsap async chunk.
 */
interface ScrollTriggerLike {
  update: () => void;
  addEventListener: (event: string, callback: () => void) => void;
  removeEventListener: (event: string, callback: () => void) => void;
}

/**
 * Smooth scroll component using Lenis
 * Provides smooth scrolling behavior for the entire page
 * Only runs in production environment
 */
class SmoothScroll {
  private lenis: Lenis | null = null;
  private rafId: number | null = null;
  private shouldPreventScroll: boolean = false;
  private refreshListener: (() => void) | null = null;
  private _scrollTrigger: ScrollTriggerLike | null = null;

  /**
   * Initialize Lenis smooth scrolling
   * Dynamically imports Lenis to keep it out of the main bundle
   */
  public async init(): Promise<void> {
    const { default: LenisClass } = await import(/* webpackChunkName: "vendor.lenis" */ 'lenis');

    // Initialize Lenis with configuration
    this.lenis = new LenisClass({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // easeOutExpo
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
      infinite: false,
      prevent: (node: Element) => {
        // Allow scrolling within mega nav panels
        if (node.closest('.mega-nav-panel')) {
          return true;
        }
        // Allow scrolling within region picker country list
        if (node.closest('.region-picker__container')) {
          return true;
        }
        // Allow scrolling within modal content
        if (node.closest('.modal__content')) {
          return true;
        }
        // Allow scrolling within search overlay
        if (node.closest('.search-overlay')) {
          return true;
        }
        // Allow scrolling within custom select dropdown
        if (node.closest('.sompo-select__dropdown')) {
          return true;
        }
        // Allow scrolling within CookieYes preference panel
        if (node.closest('.cky-preference')) {
          return true;
        }
        // Prevent body scrolling when shouldPreventScroll is true
        return this.shouldPreventScroll;
      },
    });

    // Request animation frame loop
    this.raf();

    // Add event listeners for anchor links
    this.setupAnchorLinks();

    // Listen for programmatic scroll requests from components
    this.setupCustomScrollListener();

    // Apply deferred ScrollTrigger integration if requested before Lenis loaded
    if (this._scrollTrigger) {
      this.applyScrollTrigger();
    }
  }

  /**
   * Animation frame loop for Lenis
   */
  private readonly raf = (time?: number): void => {
    if (this.lenis) {
      this.lenis.raf(time || performance.now());
    }
    this.rafId = requestAnimationFrame(this.raf);
  };

  /**
   * Connect GSAP ScrollTrigger so Lenis scroll events drive ScrollTrigger updates.
   * Called from home-hero.ts after it has loaded gsap, keeping gsap out of the
   * main bundle.
   *
   * If Lenis has not loaded yet, the reference is stored and applied once init()
   * completes.
   */
  public enableScrollTriggerIntegration(st: ScrollTriggerLike): void {
    this._scrollTrigger = st;

    if (this.lenis) {
      this.applyScrollTrigger();
    }
  }

  /**
   * Wire up the stored ScrollTrigger reference to the active Lenis instance.
   */
  private applyScrollTrigger(): void {
    if (!this.lenis || !this._scrollTrigger) return;

    // Update ScrollTrigger on every Lenis scroll event
    this.lenis.on('scroll', this._scrollTrigger.update);

    // Tell ScrollTrigger to recalculate on window resize
    this.refreshListener = () => this.lenis?.resize();
    this._scrollTrigger.addEventListener('refresh', this.refreshListener);
  }

  /**
   * Setup smooth scrolling for anchor links
   */
  private setupAnchorLinks(): void {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const target = (e.currentTarget as HTMLAnchorElement).getAttribute('href');

        if (target && target !== '#') {
          const targetElement = document.querySelector<HTMLElement>(target);
          if (targetElement && this.lenis) {
            this.lenis.scrollTo(targetElement, {
              offset: 0,
              duration: 1.2,
            });
          }
        }
      });
    });
  }

  /**
   * Setup listener for custom scroll events emitted by components
   * Components can emit 'scroll-to-target' events with a position in the detail
   */
  private setupCustomScrollListener(): void {
    document.addEventListener('scroll-to-target', ((event: Event) => {
      const customEvent = event as CustomEvent<{ position: number }>;
      const position = customEvent.detail?.position;

      if (typeof position === 'number' && this.lenis) {
        // Force Lenis to scroll to the position, overriding any state
        this.lenis.scrollTo(position, {
          duration: 0.75,
          force: true,
        });
      }
    }) as EventListener);
  }

  /**
   * Scroll to a specific target
   * @param target - CSS selector, HTMLElement, or number (scroll position)
   * @param options - Scroll options
   */
  public scrollTo(
    target: string | HTMLElement | number,
    options?: {
      offset?: number;
      duration?: number;
      easing?: (t: number) => number;
      immediate?: boolean;
      lock?: boolean;
      force?: boolean;
    }
  ): void {
    if (this.lenis) {
      this.lenis.scrollTo(target, options);
    }
  }

  /**
   * Prevent smooth scrolling (keeps Lenis running but prevents body scroll)
   * Used when modals/menus are open to allow nested scroll containers to work
   */
  public preventScroll(): void {
    this.shouldPreventScroll = true;
  }

  /**
   * Allow smooth scrolling (re-enables body scroll)
   */
  public allowScroll(): void {
    this.shouldPreventScroll = false;
  }

  /**
   * Stop smooth scrolling
   */
  public stop(): void {
    if (this.lenis) {
      this.lenis.stop();
    }
  }

  /**
   * Start smooth scrolling
   */
  public start(): void {
    if (this.lenis) {
      this.lenis.start();
    }
  }

  /**
   * Get current scroll position
   */
  public getScroll(): number {
    return this.lenis?.scroll ?? 0;
  }

  /**
   * Check if currently scrolling
   */
  public isScrolling(): boolean {
    return (this.lenis?.isScrolling as boolean) ?? false;
  }

  /**
   * Get the Lenis instance
   * Returns null if Lenis is not initialized
   */
  public getLenis(): Lenis | null {
    return this.lenis;
  }

  /**
   * Destroy the smooth scroll instance
   */
  public destroy(): void {
    if (this.lenis && this._scrollTrigger) {
      // Remove ScrollTrigger integration
      this.lenis.off('scroll', this._scrollTrigger.update);
    }

    // Remove the refresh listener to prevent memory leak
    if (this.refreshListener && this._scrollTrigger) {
      this._scrollTrigger.removeEventListener('refresh', this.refreshListener);
      this.refreshListener = null;
    }
    this._scrollTrigger = null;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (this.lenis) {
      this.lenis.destroy();
      this.lenis = null;
    }
  }
}

// Initialize smooth scroll on DOM ready
let smoothScrollInstance: SmoothScroll | null = null;

/**
 * Get the smooth scroll instance
 * Returns null if not initialized
 */
export function getSmoothScroll(): SmoothScroll | null {
  return smoothScrollInstance;
}

/**
 * Get the Lenis instance if it has been initialized
 * Returns null in development mode or if Lenis is not available
 */
export function getLenis(): Lenis | null {
  return smoothScrollInstance?.getLenis() ?? null;
}

function isCmsEditOrPreviewMode(): boolean {
  const value = document.documentElement.dataset.editOrPreviewMode;
  return value ? value.toLowerCase() === 'true' : false;
}

const initSmoothScroll = (): void => {
  if (isCmsEditOrPreviewMode()) {
    return;
  }

  if (!smoothScrollInstance) {
    smoothScrollInstance = new SmoothScroll();
    smoothScrollInstance.init();
  }
};

// Defer initialisation to after the browser has finished critical work.
// This keeps Lenis out of the main-thread critical path, reducing TBT.
function scheduleInit() {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => initSmoothScroll());
  } else {
    setTimeout(initSmoothScroll, 0);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInit);
} else {
  scheduleInit();
}
